<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { usePageContext } from '@/composables/usePageContext'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import VPurchaseStatus from '@/ui-kit/components/PurchaseStatus/PurchaseStatus.vue'
import type { PaymentModalExpose } from '~/ui-kit/components/PaymentModal/PaymentModal.vue'

interface PurchaseButton {
  buttonType: 'purchase' | 'view' | 'pay' | 'custom'
  buttonText: string
  buttonLink: string
}

interface PurchaseDiscount {
  base_price: number
  discount_percent: number
  reason?: string
}

interface PurchaseStatus {
  lock_reason: string | null
  price: number | null
  status_code: string
  button: PurchaseButton | null
  discount: PurchaseDiscount | null
}

interface SeminarReview {
  name: string
  image: string
  body: string
}

interface SeminarDescriptionContext {
  seminar_id: number
  seminar_name: string
  purchase_status: PurchaseStatus
  description_html: string
  reviews: SeminarReview[]
}

type PurchaseMaterialsHandler = (id: number, price: number, title: string) => void

declare global {
  interface Window {
    purchase_materials?: PurchaseMaterialsHandler
  }
}

const ctx = usePageContext<SeminarDescriptionContext>()
const paymentModal = ref<PaymentModalExpose | null>(null)

const sliderConfig = {
  slidesPerView: 'auto',
  spaceBetween: 16,
  watchOverflow: true,
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev'
  }
}

const showReviews = computed(() => ctx.seminar_name === 'Базовый семинар' && ctx.reviews.length > 0)

const purchaseMaterials: PurchaseMaterialsHandler = (id, price, title) => {
  paymentModal.value?.open({
    order: { name: `Лекции семинара ${title}`, price },
    query: { seminar_id: id }
  })
}

onMounted(() => {
  window.purchase_materials = purchaseMaterials
})

onBeforeUnmount(() => {
  delete window.purchase_materials
})
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" rules-url="/website/documents/oferta" />

    <div v-cloak class="content seminar-description">
      <v-payment-modal ref="paymentModal" url="/seminars/request_purchase_materials" />

      <h1 class="header">О семинаре</h1>
      <h2 class="title">{{ ctx.seminar_name }}</h2>

      <VPurchaseStatus
        :lock-reason="ctx.purchase_status.lock_reason || ''"
        :price="ctx.purchase_status.price"
        :status-code="ctx.purchase_status.status_code"
        :button="ctx.purchase_status.button"
        :discount="ctx.purchase_status.discount"
      />

      <div class="text" v-html="ctx.description_html" />

      <section v-if="showReviews" class="seminar-reviews">
        <h3 class="seminar-section-title mt-4">Отзывы участников семинара</h3>

        <v-slider :config="sliderConfig">
          <article v-for="review in ctx.reviews" :key="review.name" class="swiper-slide seminar-reviews__slide">
            <div class="seminar-reviews__author-info">
              <img class="seminar-reviews__img" :src="review.image" />
              <h4 v-text="review.name" />
            </div>

            <p v-text="review.body" />
          </article>
        </v-slider>
      </section>

      <VPurchaseStatus
        secondary
        :lock-reason="ctx.purchase_status.lock_reason || ''"
        :price="ctx.purchase_status.price"
        :status-code="ctx.purchase_status.status_code"
        :button="ctx.purchase_status.button"
        :discount="ctx.purchase_status.discount"
      />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/seminars/seminar_description.scss' as *;
</style>
